<template>
  <div class="flex flex-col">
    <AppModal v-model="showAddModal" title="Add Exempt Keyword">
      <form @submit.prevent="saveKeyword">
        <div class="space-y-4">
          <div>
            <label class="text-xs font-semibold text-gray-600 uppercase tracking-wide">Keyword</label>
            <input
              v-model="form.keyword"
              type="text"
              placeholder="e.g. my-custom-endpoint"
              class="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary bg-gray-50"
              required
            />
          </div>
          <div class="flex flex-wrap justify-end gap-2">
            <button type="button" class="btn-sm-primary" @click="closeAddModal">
              Cancel
            </button>
            <button type="submit" class="btn-primary" :disabled="saving">
              <i v-if="saving" class="fa fa-spinner fa-spin"></i>
              <i v-else class="fa-solid fa-save"></i>
              <span>Save</span>
            </button>
          </div>
        </div>
      </form>
    </AppModal>

    <div class="flex flex-wrap items-center justify-between gap-3 mb-4">
      <p class="text-sm text-gray-600 flex-1 min-w-0">
        Manage dynamic exempt keywords on the URSB API Gateway blocker bypass list. Matching endpoints bypass gateway blocking rules.
      </p>
      <div class="flex flex-wrap items-center gap-2 shrink-0">
        <button type="button" class="btn" :disabled="loading" @click="loadKeywords">
          <i class="fa-solid fa-rotate-right" :class="{ 'fa-spin': loading }"></i>
          <span>Refresh</span>
        </button>
        <button type="button" class="btn-primary" @click="openAddModal">
          <i class="fa-solid fa-plus"></i>
          <span>Add Keyword</span>
        </button>
      </div>
    </div>

    <div v-if="loading && keywords.length === 0" class="w-full bg-gray-200 rounded-full h-1 overflow-hidden mb-4">
      <div class="bg-primary h-1 rounded-full animate-pulse" style="width: 100%"></div>
    </div>

    <div class="bg-white rounded border border-gray-200 overflow-hidden">
      <div class="overflow-x-auto">
        <table class="w-full text-xs">
          <thead class="bg-gray-50 sticky top-0">
            <tr>
              <th class="text-left py-3 px-4 font-semibold text-gray-700 border-b border-gray-200 w-16">#</th>
              <th class="text-left py-3 px-4 font-semibold text-gray-700 border-b border-gray-200">Keyword</th>
              <th class="text-center py-3 px-4 font-semibold text-gray-700 border-b border-gray-200 w-32">Actions</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="(keyword, idx) in paginatedItems"
              :key="keyword"
              class="hover:bg-gray-50 transition"
            >
              <td class="py-3 px-4 border-b border-gray-200">{{ rowNumber(idx) }}</td>
              <td class="py-3 px-4 border-b border-gray-200 font-medium break-all">{{ keyword }}</td>
              <td class="py-3 px-4 border-b border-gray-200">
                <div class="flex items-center justify-center">
                  <button
                    type="button"
                    class="btn-sm-danger"
                    :disabled="removingKeyword === keyword"
                    @click="confirmRemove(keyword)"
                  >
                    <i v-if="removingKeyword === keyword" class="fa fa-spinner fa-spin"></i>
                    <i v-else class="fa-solid fa-trash"></i>
                    <span>Remove</span>
                  </button>
                </div>
              </td>
            </tr>
            <tr v-if="keywords.length === 0 && !loading">
              <td colspan="3" class="py-8 px-4 text-center text-gray-500">
                No exempt keywords configured. Click "Add Keyword" to create one.
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <GatewayTablePagination
      :current-page="currentPage"
      :total-pages="totalPages"
      :total-elements="totalElements"
      :page-size="pageSize"
      :range-start="rangeStart"
      :range-end="rangeEnd"
      @previous="previous"
      @next="next"
      @update:page-size="onPageSizeUpdate"
    />
  </div>
</template>

<script setup lang="ts">
import { ref } from "vue";
import AppModal from "@/components/ui/AppModal.vue";
import GatewayTablePagination from "./GatewayTablePagination.vue";
import { useClientPagination } from "./useClientPagination";
import { useConfirmDialog, useToast } from "@/composables";
import {
  addExemptKeyword,
  extractErrorMessage,
  fetchExemptKeywords,
  removeExemptKeyword,
} from "./exemptKeywordsApi";

const { successToast, errorToast, warningToast } = useToast();
const { showConfirm } = useConfirmDialog();

const keywords = ref<string[]>([]);
const loading = ref(false);
const saving = ref(false);
const removingKeyword = ref<string | null>(null);
const showAddModal = ref(false);
const form = ref({ keyword: "" });

const {
  currentPage,
  pageSize,
  totalElements,
  totalPages,
  paginatedItems,
  rangeStart,
  rangeEnd,
  previous,
  next,
  resetPage,
} = useClientPagination(keywords, 25);

const onPageSizeUpdate = (size: number) => {
  pageSize.value = size;
  resetPage();
};

const rowNumber = (idx: number) => (currentPage.value - 1) * pageSize.value + idx + 1;

const loadKeywords = async () => {
  loading.value = true;
  try {
    keywords.value = await fetchExemptKeywords();
    resetPage();
  } catch (error: unknown) {
    keywords.value = [];
    resetPage();
    errorToast("Error", extractErrorMessage(error, "Could not load exempt keywords"));
  } finally {
    loading.value = false;
  }
};

const openAddModal = () => {
  form.value.keyword = "";
  showAddModal.value = true;
};

const closeAddModal = () => {
  showAddModal.value = false;
  form.value.keyword = "";
};

const saveKeyword = async () => {
  const keyword = form.value.keyword.trim();
  if (!keyword) {
    warningToast("Validation", "Enter a keyword");
    return;
  }
  saving.value = true;
  try {
    await addExemptKeyword(keyword);
    successToast("Success", "Exempt keyword added");
    closeAddModal();
    await loadKeywords();
  } catch (error: unknown) {
    errorToast("Error", extractErrorMessage(error, "Could not add exempt keyword"));
  } finally {
    saving.value = false;
  }
};

const confirmRemove = async (keyword: string) => {
  const confirmed = await showConfirm(
    `Remove exempt keyword "${keyword}" from the gateway bypass list?`,
    {
      title: "Remove Exempt Keyword",
      variant: "danger",
      okTitle: "Remove",
      cancelTitle: "Cancel",
    }
  );
  if (!confirmed) return;

  removingKeyword.value = keyword;
  try {
    await removeExemptKeyword(keyword);
    successToast("Success", "Exempt keyword removed");
    await loadKeywords();
  } catch (error: unknown) {
    errorToast("Error", extractErrorMessage(error, "Could not remove exempt keyword"));
  } finally {
    removingKeyword.value = null;
  }
};

defineExpose({ refresh: loadKeywords });
</script>
